
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Edit, Trash2, UserPlus, MoreHorizontal } from 'lucide-react';
import { AdminUser } from '@/contexts/auth/types';
import AdminLayout from './AdminLayout';
import { fetchUsers } from '@/services/userService';
import { useToast } from '@/hooks/use-toast';
import { getToken } from '@/services/apiService';
import { decodeToken } from '@/utils/decodeToken';
import UserEditDialog from '@/components/admin/UserEditDialog';
import UserDeleteDialog from '@/components/admin/UserDeleteDialog';
import UserAddDialog from '@/components/admin/UserAddDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { UserRoles } from '@/contexts/auth/enums';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Dialog states
  const [userToEdit, setUserToEdit] = useState<AdminUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { data: users = [], isLoading, error, isError } = useQuery({
    queryKey: ['users'],
    queryFn: () => fetchUsers()
  });

  // Decode and log the token
  useEffect(() => {
    const token = getToken();
    if (token) {
      const decodedToken = decodeToken(token);
      console.log('Decoded Token:', decodedToken);
    }
  }, []);

  // Show error toast when there's an error
  useEffect(() => {
    if (isError && error) {
      toast({
        title: 'Hiba',
        description: 'A felhasználók betöltése sikertelen.',
        variant: 'destructive',
      });
    }
  }, [isError, error, toast]);

  const handleEditUser = (user: AdminUser) => {
    setUserToEdit(user);
    setIsEditDialogOpen(true);
  };

  const handleDeleteUser = (user: AdminUser) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleUserUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const getRoleBadgeVariant = (role: UserRoles) => {
    switch (role) {
      case UserRoles.ADMIN:
        return 'destructive';
      case UserRoles.ORGANIZER:
        return 'default';
      case UserRoles.USER:
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: UserRoles) => {
    switch (role) {
      case UserRoles.ADMIN:
        return 'Admin';
      case UserRoles.ORGANIZER:
        return 'Organizer';
      case UserRoles.USER:
        return 'User';
      default:
        return role;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'outline';
      case 'inactive':
        return 'secondary';
      case 'suspended':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const filteredUsers = !isLoading && !isError && users.length ? users.filter((user: AdminUser) => {
    const matchesSearch = searchTerm === '' ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.userProfile?.name?.firstName && user.userProfile.name.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.userProfile?.name?.lastName && user.userProfile.name.lastName.toLowerCase().includes(searchTerm.toLowerCase()));

    // Fix role filtering to work with array
    const matchesRole = roleFilter === undefined ||
      (user.roles && user.roles.some((role: UserRoles) => role === roleFilter));
    const matchesStatus = statusFilter === undefined || user.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  }) : [];

  return (
    <AdminLayout>
      <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold tracking-tight">User Management</h2>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              Manage user accounts, roles, and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={""}>All Roles</SelectItem>
                  <SelectItem value={UserRoles.ADMIN}>Admin</SelectItem>
                  <SelectItem value={UserRoles.ORGANIZER}>Organizer</SelectItem>
                  <SelectItem value={UserRoles.USER}>User</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Created</TableHead>
                    <TableHead className="hidden md:table-cell">Last Login</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : isError ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center text-red-500">
                        Error loading users
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user: AdminUser) => (
                      <TableRow key={user['@id']}>
                        <TableCell className="font-medium">
                          {user.userProfile?.name?.academicTitle ? `${user.userProfile.name.academicTitle} ` : ''}
                          {user.userProfile?.name?.firstName || ''} {user.userProfile?.name?.lastName || ''}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.roles.map((role: UserRoles, index: number) => (
                            <Badge key={index} variant={getRoleBadgeVariant(role)}>
                              {getRoleLabel(role)}
                            </Badge>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(user.status)}>
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteUser(user)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <UserEditDialog
        user={userToEdit}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onUserUpdated={handleUserUpdated}
      />

      <UserDeleteDialog
        user={userToDelete}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onUserDeleted={handleUserUpdated}
      />

      <UserAddDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onUserAdded={handleUserUpdated}
      />
    </AdminLayout>
  );
};

export default UserManagement;
